import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Stack, Divider, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Refresh, ContentCopy, AccountBalance } from '@mui/icons-material';
import walletService from 'api/walletService';
import { toast } from 'react-toastify';

const VirtualAccountManagement = ({ walletId, accountDetails }) => {
    const queryClient = useQueryClient();
    const [regenerating, setRegenerating] = useState(false);

    // If accountDetails is passed, use it, otherwise we could fetch here
    // But usually we get it from the detail page data

    const regenerateMutation = useMutation({
        mutationFn: () => walletService.adminRegenerateVirtualAccount(walletId),
        onStart: () => setRegenerating(true),
        onSuccess: (data) => {
            toast.success('Virtual account regenerated successfully');
            queryClient.invalidateQueries(['adminVendorDetails']);
            queryClient.invalidateQueries(['adminCustomerDetails']);
            queryClient.invalidateQueries(['adminRiderDetails']);
        },
        onError: (err) => {
            console.error(err);
        },
        onSettled: () => setRegenerating(false)
    });

    if (!walletId) return <Alert severity="warning">Wallet ID missing for virtual account management</Alert>;

    return (
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eee' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                    Monnify Virtual Account
                </Typography>
                <Tooltip title="Force Regenerate">
                    <IconButton size="small" color="primary" onClick={() => regenerateMutation.mutate()} disabled={regenerating}>
                        {regenerating ? <CircularProgress size={20} /> : <Refresh />}
                    </IconButton>
                </Tooltip>
            </Stack>

            {accountDetails?.accountNumber ? (
                <Stack spacing={2}>
                    <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <AccountBalance color="secondary" />
                            <Box flex={1}>
                                <Typography variant="caption" color="text.secondary">
                                    Account Number
                                </Typography>
                                <Typography variant="h5" fontWeight={700}>
                                    {accountDetails.accountNumber}
                                    <IconButton
                                        size="small"
                                        sx={{ ml: 1 }}
                                        onClick={() => {
                                            navigator.clipboard.writeText(accountDetails.accountNumber);
                                            toast.success('Copied to clipboard');
                                        }}
                                    >
                                        <ContentCopy fontSize="inherit" />
                                    </IconButton>
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Stack direction="row" spacing={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Bank Name
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {accountDetails.bankName}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Account Name
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {accountDetails.accountName}
                            </Typography>
                        </Box>
                    </Stack>

                    <Divider />

                    <Typography variant="caption" color="text.secondary">
                        Reference: {accountDetails.accountReference}
                    </Typography>
                </Stack>
            ) : (
                <Box textAlign="center" py={2}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        No virtual account assigned to this user yet.
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                        onClick={() => regenerateMutation.mutate()}
                        disabled={regenerating}
                    >
                        {regenerating ? 'Generating...' : 'Generate Account Now'}
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

export default VirtualAccountManagement;
